import { useMemo, useState, type FormEvent, type JSX, type ReactNode } from "react";
import {
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  DeleteWithConfirm,
  Inline,
  Stack,
  StatePill,
  TextInput,
  type DataTableColumn,
  type StatePillTone,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";

import { formatTimestampSafe } from "../../../locale-format.js";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../../lib/api.js";
import { useSetMemberRole } from "../../../members/useSetMemberRole.js";
import { useSetMemberStatus } from "../../../members/useSetMemberStatus.js";
import { useGtccHeadTabs } from "../control/HeadTabsContext.js";
import {
  GTCC_INVITATION_STATUSES,
  GTCC_MEMBER_ROLES,
  GTCC_MEMBER_STATUSES,
  gtccMembersTruncated,
  useGtccCreateInvitation,
  useGtccHardDeleteInvitation,
  useGtccInvitations,
  useGtccMembers,
  useGtccResendInvitation,
  useGtccRevokeInvitation,
  type GtccInvitation,
  type GtccInvitationStatus,
  type GtccMember,
  type GtccMemberRole,
  type GtccMemberStatus,
} from "./useGtccPeople.js";

/**
 * B-GTCC-A14 / A-GTCC-CHECKS1 — register rows 23 ("Einladungen verwalten") and 24
 * ("Plattform-Mitglieder (People)") as one read-only page, the way gtcc1
 * composes them in `apps/admin/src/people/PeoplePage.tsx`: members and
 * invitations are two halves of the same question, who is in this tenant and
 * who has been asked to be.
 *
 * A-GTCC-CHECKS1 and C-GTCC-24-WRITE1 close both halves of that gap. Row 23
 * was missing three verbs — "kein Einladen, Erneut-Senden oder Widerrufen" —
 * row 24 was missing suspend and re-role, and each package ported exactly its
 * own half onto this page. Both rows therefore leave TEIL; what stays out is
 * member remove and member exit, and the card at the foot of the page names
 * that remainder instead of implying the page is finished.
 *
 * The page is therefore no longer read-only on either half, and says so: the
 * header pill names both writable halves instead of promising "nur lesend".
 *
 * What is measured rather than assumed:
 *
 *   - `PeoplePage.tsx` issues no request of its own; it composes
 *     `<MembersPage />` and `<InvitationsPage />`. The endpoints behind those
 *     are `GET /v1/admin/members`
 *     (`services/api/src/modules/admin/routes.ts:411`) and
 *     `GET /v1/admin/invitations`
 *     (`services/api/src/modules/identity/invitations.routes.ts:161`).
 *   - The two GETs are gated differently:
 *     `services/api/src/modules/admin/members.service.ts:59` requires
 *     `tenant.read`, `services/api/src/modules/identity/invitations.service.ts:58`
 *     requires `tenant.members.write`. A reader with read-only rights
 *     therefore sees the member table and a 403 on the invitation table. That
 *     is a backend property this page must not paper over, so each section
 *     carries its own error state and names the permission the gate wants
 *     instead of blanking the page.
 *   - The member aggregates (`total`, `byRole`, `byStatus`) are counted over
 *     the whole tenant, the row list is cut off by `limit`
 *     (`services/api/src/modules/admin/members.repo.ts:246` aggregates with
 *     no limit at all). The page states the gap when there is one, so a
 *     truncated table never reads as a headcount.
 *   - Invitations carry no aggregate at all — the route answers `{ items }`
 *     and nothing else — so the status counts here are counted over exactly
 *     the rows shown, and are labelled as such.
 *
 * The write surface is deliberately narrow, and the test pins the narrowness:
 *
 *   - Every control on the invitation card is an invite, a resend, a revoke or
 *     the hard delete E-GTCC-23-HARD1 added; the member table holds exactly the role `select` and the suspend or
 *     reactivate button that C-GTCC-24-WRITE1 ported, and nothing else. Both
 *     halves still show absence rather than a disabled control where a write
 *     is not offered.
 *   - Resend and revoke appear on `pending` rows only. That mirrors the route,
 *     which answers 409 `invite_invalid` for any other status
 *     (`services/api/src/modules/identity/invitations.routes.ts:318`); the
 *     backend stays the authority and the 409 is rendered, not hidden.
 *   - Revoke is the soft one and never sends `?confirm=hard`; the row stays
 *     as the record of who was asked. The hard delete (E-GTCC-23-HARD1) is
 *     the separate `<DeleteWithConfirm>` on every row, any status, because
 *     the route answers 204 regardless
 *     (`services/api/src/modules/identity/invitations.routes.ts:310-345`);
 *     it only fires after the row's e-mail was typed, the same gate
 *     `GtccTenantsView` puts in front of removing a member.
 *   - The two gtcc1 modules next door (`useInvitations.ts`, `useMembers.ts`)
 *     are still not imported. They carry their own cache keys and their own
 *     hard-delete path; `useGtccPeople.ts` keeps its own and invalidates the
 *     gtcc1 key alongside, so both surfaces agree after a write.
 *
 * Revoke is confirmed by a dialog, the hard delete by typing the address.
 * Resend is idempotent and costs the invitee a second mail; a dialog in front
 * of it would be ceremony, not a safeguard.
 *
 * Not wired on purpose: `registry.ts` and `GtccShell.tsx` stay untouched (lift
 * lock). Unlike A13 there is not even a key to fill — the registry holds 23
 * view keys and none of them is people, members or invitations — so the
 * wiring step will have to add a key, not just a `component:`.
 *
 * C-GTCC-24-WRITE1 — register row 24 moves from TEIL to JA. The member half
 * gains exactly the two write affordances gtcc1's members page owns, and no
 * more:
 *
 *   - `PATCH /v1/admin/members/:id/role`
 *     (`apps/admin/src/members/useSetMemberRole.ts`)
 *   - `PATCH /v1/admin/members/:id/status`
 *     (`apps/admin/src/members/useSetMemberStatus.ts`)
 *
 * Both mutations are those gtcc1 hooks themselves, imported rather than
 * re-implemented. Each of those two files exports one write hook and nothing
 * else, so importing them drags no further write path along — which is exactly
 * why `useInvitations.ts` and `useMembers.ts` stay unimported: those two ship
 * several write hooks from one file, and that reason still holds. The server
 * gate `tenant.members.write`
 * (`services/api/src/modules/admin/routes.ts:442` and `:495`) is unchanged; no
 * route, no permission and no DTO is added here.
 *
 * The invitation half is no longer the read-only one it was when this
 * paragraph was first written: A-GTCC-CHECKS1 gave it an invite form plus a
 * resend and a revoke on `pending` rows. The two success banners are kept
 * apart on purpose — `gtcc-ppl-members-success` and
 * `gtcc-ppl-invitations-success` hold their own state, so a member write
 * never reports itself inside the invitation card or the other way round.
 *
 * Two deliberate differences from the tenant-scoped members card
 * (`views/ten/GtccTenantsView.tsx`):
 *
 *   - No client-side last-active-owner shield. There the whole member list is
 *     in hand; here `items` is capped at `GTCC_MEMBER_LIMIT` and the
 *     aggregates are not cross-tabbed — `byRole.owner` and `byStatus.active`
 *     are counted separately — so a shield computed from what is on screen
 *     would be a guess. The 409 `last_active_owner`
 *     (`services/api/src/modules/admin/routes.ts`) is the only authority and
 *     gets its own message.
 *   - No remove and no exit. gtcc1's members page offers neither
 *     (`apps/admin/src/members/MembersPage.tsx` imports exactly
 *     `useSetMemberRole` and `useSetMemberStatus`), and porting a destructive
 *     path the source surface does not have would be a new capability, not a
 *     port.
 */

const ROLE_TONES: Readonly<Record<GtccMemberRole, StatePillTone>> = {
  owner: "info",
  admin: "info",
  dispatcher: "neutral",
  viewer: "neutral",
};

const MEMBER_STATUS_TONES: Readonly<Record<GtccMemberStatus, StatePillTone>> = {
  active: "success",
  invited: "info",
  suspended: "warn",
};

/**
 * `expired` is warn rather than neutral: an invitation that ran out is work
 * left undone, not a closed case like `accepted` or a decision like `revoked`.
 */
const INVITATION_STATUS_TONES: Readonly<Record<GtccInvitationStatus, StatePillTone>> = {
  pending: "info",
  accepted: "success",
  revoked: "neutral",
  expired: "warn",
};

/**
 * One member write waiting for the operator's confirmation. The role case
 * carries the target role because the select has already moved on screen; the
 * two status cases carry none, the kind is the whole instruction.
 */
type MemberWriteAction =
  | { readonly kind: "role"; readonly member: GtccMember; readonly role: GtccMemberRole }
  | { readonly kind: "suspend"; readonly member: GtccMember }
  | { readonly kind: "reactivate"; readonly member: GtccMember };

/* Stable empty fallbacks: `?? []` would hand out a fresh array on every
   render while a query is loading or failed, and the memo below would then
   recount on every render for nothing. */
const EMPTY_MEMBERS: readonly GtccMember[] = [];
const EMPTY_INVITATIONS: readonly GtccInvitation[] = [];

function isForbidden(error: Error | null): boolean {
  return error instanceof ApiError && error.status === 403;
}

/**
 * The design system ships `Button`, `TextInput` and `ConfirmDialog` but no
 * `Select` primitive (`packages/design-system/src/index.ts`), so the role
 * picker is a native `<select>` painted with the same tokens — the identical
 * choice `GtccTenantsView` makes for its member role picker.
 */
/** The frame of `TextInput`, repeated here because the design system ships no Select. */
const SELECT_STYLE = {
  paddingBlock: 10,
  paddingInline: 12,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-md)",
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  font: "inherit",
} as const;

/** Least privilege wins the default: an invite that needs more is one click away. */
const DEFAULT_INVITE_ROLE: GtccMemberRole = "viewer";

function SectionCard({
  testId,
  headingId,
  title,
  subtitle,
  children,
}: {
  readonly testId: string;
  readonly headingId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <section aria-labelledby={headingId} data-testid={testId}>
      <Card elevation={1}>
        <Stack gap={3}>
          <Stack gap={1}>
            <h3 id={headingId} style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}>
              {title}
            </h3>
            <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>{subtitle}</p>
          </Stack>
          {children}
        </Stack>
      </Card>
    </section>
  );
}

/** One counted bucket: a label and the number of rows in it. */
function Count({
  label,
  value,
  tone,
}: {
  readonly label: string;
  readonly value: number;
  readonly tone: StatePillTone;
}): JSX.Element {
  return <StatePill tone={tone} label={`${label}: ${value}`} />;
}

export function GtccPeopleView(): JSX.Element {
  const { t, locale } = useLocale();
  const headTabs = useGtccHeadTabs();

  const qc = useQueryClient();
  const members = useGtccMembers();
  const invitations = useGtccInvitations();

  const createInvitation = useGtccCreateInvitation();
  const resendInvitation = useGtccResendInvitation();
  const revokeInvitation = useGtccRevokeInvitation();
  const hardDeleteInvitation = useGtccHardDeleteInvitation();

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<GtccMemberRole>(DEFAULT_INVITE_ROLE);
  const [pendingRevoke, setPendingRevoke] = useState<GtccInvitation | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  /* One in-flight write at a time: the four mutations share a list, and a
     second click while the first is still open would race the invalidation. */
  const writeBusy =
    createInvitation.isPending ||
    resendInvitation.isPending ||
    revokeInvitation.isPending ||
    hardDeleteInvitation.isPending;

  const [pendingAction, setPendingAction] = useState<MemberWriteAction | null>(null);
  const [memberSuccess, setMemberSuccess] = useState<string | null>(null);

  const setRole = useSetMemberRole();
  const setStatus = useSetMemberStatus();

  /*
    The gtcc1 hooks invalidate their own key `["admin", "members"]`; this view
    reads under `["gtcc", "ppl", "members", …]`, so the second invalidation is
    the one that refreshes the table on screen.
  */
  const invalidateMembers = (): void => {
    void qc.invalidateQueries({ queryKey: ["gtcc", "ppl", "members"] });
  };

  const writePending = setRole.isPending || setStatus.isPending;
  const writeFailure: Error | null = setRole.error ?? setStatus.error ?? null;
  const writeError =
    writeFailure === null
      ? null
      : writeFailure instanceof ApiError && writeFailure.status === 409
        ? t("admin.gtcc.ppl.members.write.error_last_owner")
        : t("admin.gtcc.ppl.members.write.error");

  const beginAction = (action: MemberWriteAction): void => {
    setRole.reset();
    setStatus.reset();
    setMemberSuccess(null);
    setPendingAction(action);
  };

  const closeDialog = (): void => {
    if (writePending) return;
    setRole.reset();
    setStatus.reset();
    setPendingAction(null);
  };

  const runAction = (): void => {
    if (!pendingAction) return;
    setMemberSuccess(null);
    if (pendingAction.kind === "role") {
      setRole.mutate(
        { memberId: pendingAction.member.id, role: pendingAction.role },
        {
          onSuccess: () => {
            setMemberSuccess(t("admin.gtcc.ppl.members.write.success_role"));
            setPendingAction(null);
            invalidateMembers();
          },
        },
      );
      return;
    }
    const suspending = pendingAction.kind === "suspend";
    setStatus.mutate(
      { memberId: pendingAction.member.id, status: suspending ? "suspended" : "active" },
      {
        onSuccess: () => {
          setMemberSuccess(
            suspending
              ? t("admin.gtcc.ppl.members.write.success_suspended")
              : t("admin.gtcc.ppl.members.write.success_reactivated"),
          );
          setPendingAction(null);
          invalidateMembers();
        },
      },
    );
  };

  const confirmBody = ((): string => {
    if (!pendingAction) return "";
    const member = pendingAction.member.email || pendingAction.member.id;
    if (pendingAction.kind === "role") {
      return t("admin.gtcc.ppl.members.write.confirm_role", {
        member,
        role: t(`admin.gtcc.ppl.role.${pendingAction.role}`),
      });
    }
    if (pendingAction.kind === "suspend") {
      return t("admin.gtcc.ppl.members.write.confirm_suspend", { member });
    }
    return t("admin.gtcc.ppl.members.write.confirm_reactivate", { member });
  })();

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }),
    [locale],
  );

  const memberRows = members.data?.items ?? EMPTY_MEMBERS;
  const invitationRows = invitations.data?.items ?? EMPTY_INVITATIONS;

  /*
    Counted over the rows on screen, because the route offers nothing else.
    Every status is listed even at zero: a bucket that disappears when empty
    turns "no revoked invitations" into "no such thing as revoked".
  */
  const invitationCounts = useMemo(() => {
    const counts: Record<GtccInvitationStatus, number> = {
      pending: 0,
      accepted: 0,
      revoked: 0,
      expired: 0,
    };
    for (const row of invitationRows) {
      if (row.status in counts) counts[row.status] += 1;
    }
    return counts;
  }, [invitationRows]);

  const memberColumns: readonly DataTableColumn<GtccMember>[] = [
    {
      id: "displayName",
      header: t("admin.gtcc.ppl.members.col.name"),
      cell: (row) => row.displayName || "—",
    },
    {
      id: "email",
      header: t("admin.gtcc.ppl.members.col.email"),
      cell: (row) => row.email,
    },
    {
      id: "role",
      header: t("admin.gtcc.ppl.members.col.role"),
      cell: (row) => <StatePill tone={ROLE_TONES[row.role] ?? "neutral"} label={row.role} />,
    },
    {
      id: "status",
      header: t("admin.gtcc.ppl.members.col.status"),
      cell: (row) => (
        <StatePill tone={MEMBER_STATUS_TONES[row.status] ?? "neutral"} label={row.status} />
      ),
    },
    {
      id: "lastActive",
      header: t("admin.gtcc.ppl.members.col.last_active"),
      cell: (row) =>
        row.lastActive ? (
          <time dateTime={row.lastActive}>
            {formatTimestampSafe(row.lastActive, (d) => dateFormatter.format(d))}
          </time>
        ) : (
          t("admin.gtcc.ppl.members.never_active")
        ),
    },
    {
      id: "actions",
      header: t("admin.gtcc.ppl.members.col.actions"),
      cell: (row) => (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "var(--gt-sys-space-2)",
          }}
        >
          <select
            value={row.role}
            disabled={writePending}
            aria-label={t("admin.gtcc.ppl.members.write.role_label")}
            data-testid={`gtcc-ppl-member-role-${row.id}`}
            onChange={(event) => {
              const next = event.currentTarget.value as GtccMemberRole;
              if (next === row.role) return;
              beginAction({ kind: "role", member: row, role: next });
            }}
            style={{
              padding: "var(--gt-sys-space-1) var(--gt-sys-space-2)",
              borderRadius: "var(--gt-sys-radius-sm)",
              border: "1px solid var(--gt-sys-border)",
              background: "var(--gt-sys-surface)",
              color: "var(--gt-sys-text)",
              font: "inherit",
            }}
          >
            {GTCC_MEMBER_ROLES.map((role) => (
              <option key={role} value={role}>
                {t(`admin.gtcc.ppl.role.${role}`)}
              </option>
            ))}
          </select>
          {row.status === "suspended" ? (
            <Button
              variant="secondary"
              disabled={writePending}
              data-testid={`gtcc-ppl-member-reactivate-${row.id}`}
              onClick={() => beginAction({ kind: "reactivate", member: row })}
            >
              {t("admin.gtcc.ppl.members.write.reactivate")}
            </Button>
          ) : (
            <Button
              variant="secondary"
              disabled={writePending}
              data-testid={`gtcc-ppl-member-suspend-${row.id}`}
              onClick={() => beginAction({ kind: "suspend", member: row })}
            >
              {t("admin.gtcc.ppl.members.write.suspend")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const invitationColumns: readonly DataTableColumn<GtccInvitation>[] = [
    {
      id: "email",
      header: t("admin.gtcc.ppl.invitations.col.email"),
      cell: (row) => row.email,
    },
    {
      id: "role",
      header: t("admin.gtcc.ppl.invitations.col.role"),
      cell: (row) => <StatePill tone={ROLE_TONES[row.role] ?? "neutral"} label={row.role} />,
    },
    {
      id: "status",
      header: t("admin.gtcc.ppl.invitations.col.status"),
      cell: (row) => (
        <StatePill tone={INVITATION_STATUS_TONES[row.status] ?? "neutral"} label={row.status} />
      ),
    },
    {
      id: "invitedBy",
      header: t("admin.gtcc.ppl.invitations.col.invited_by"),
      cell: (row) => row.invitedByEmail,
    },
    {
      id: "tokenExpiresAt",
      header: t("admin.gtcc.ppl.invitations.col.expires"),
      cell: (row) => (
        <time dateTime={row.tokenExpiresAt}>
          {formatTimestampSafe(row.tokenExpiresAt, (d) => dateFormatter.format(d))}
        </time>
      ),
    },
    {
      id: "actions",
      header: t("admin.gtcc.ppl.invitations.write.col_actions"),
      cell: (row) => (
        <Inline gap={2} wrap>
          {row.status === "pending" ? (
            <>
              <Button
                size="sm"
                variant="secondary"
                disabled={writeBusy}
                data-testid={`gtcc-ppl-invitation-resend-${row.id}`}
                onClick={() => {
                  setInviteSuccess(null);
                  resendInvitation.mutate(row.id, {
                    onSuccess: () =>
                      setInviteSuccess(t("admin.gtcc.ppl.invitations.write.success_resent")),
                  });
                }}
              >
                {t("admin.gtcc.ppl.invitations.write.resend")}
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={writeBusy}
                data-testid={`gtcc-ppl-invitation-revoke-${row.id}`}
                onClick={() => {
                  setInviteSuccess(null);
                  setPendingRevoke(row);
                }}
              >
                {t("admin.gtcc.ppl.invitations.write.revoke")}
              </Button>
            </>
          ) : (
            <span style={{ color: "var(--gt-sys-text-muted)" }}>
              {t("admin.gtcc.ppl.invitations.write.not_pending")}
            </span>
          )}
          {/* E-GTCC-23-HARD1 — hard delete on every status, gated by typing
              the address (pattern: GtccTenantsView member removal). */}
          <DeleteWithConfirm
            triggerLabel={t("admin.gtcc.ppl.invitations.write.delete")}
            entityLabel={t("admin.gtcc.ppl.invitations.write.delete_entity")}
            identifierFieldLabel={t("admin.gtcc.ppl.invitations.write.delete_identifier_field")}
            identifierExpected={row.email}
            warningBody={t("admin.gtcc.ppl.invitations.write.delete_warning")}
            confirmLabel={t("delete.confirm.confirm")}
            cancelLabel={t("common.cancel")}
            typeToConfirmHint={t("delete.confirm.type_to_confirm")}
            errorMessages={{
              forbidden: t("delete.error.forbidden"),
              notFound: t("delete.error.not_found"),
              generic: t("delete.error.generic"),
            }}
            triggerSize="sm"
            testIdPrefix={`gtcc-ppl-invitation-delete-${row.id}`}
            onConfirm={() => {
              setInviteSuccess(null);
              return hardDeleteInvitation.mutateAsync(row.id).then(() => {
                setInviteSuccess(t("admin.gtcc.ppl.invitations.write.success_deleted"));
              });
            }}
          />
        </Inline>
      ),
    },
  ];

  const membersError = members.isError
    ? {
        title: isForbidden(members.error)
          ? t("admin.gtcc.ppl.members.forbidden")
          : t("admin.gtcc.ppl.members.error"),
      }
    : null;
  const invitationsError = invitations.isError
    ? {
        title: isForbidden(invitations.error)
          ? t("admin.gtcc.ppl.invitations.forbidden")
          : t("admin.gtcc.ppl.invitations.error"),
      }
    : null;

  /*
    Three failure shapes, three answers, all measured against the route:
    403 is the `tenant.members.write` gate, 409 on the POST is "already
    invited" (`invitations.service.ts` rejects a second pending row for the
    same address), 409 on the DELETE is `invite_invalid` — the row stopped
    being pending between render and click. Anything else is the generic one.
  */
  const inviteError = (() => {
    const failure = createInvitation.error;
    if (!failure) return null;
    if (failure instanceof ApiError && failure.status === 403) {
      return t("admin.gtcc.ppl.invitations.write.error_forbidden");
    }
    if (failure instanceof ApiError && failure.status === 409) {
      return t("admin.gtcc.ppl.invitations.write.error_duplicate");
    }
    return t("admin.gtcc.ppl.invitations.write.error");
  })();

  const rowActionError = (() => {
    const failure = revokeInvitation.error ?? resendInvitation.error;
    if (!failure) return null;
    if (failure instanceof ApiError && failure.status === 403) {
      return t("admin.gtcc.ppl.invitations.write.error_forbidden");
    }
    if (failure instanceof ApiError && failure.status === 409) {
      return t("admin.gtcc.ppl.invitations.write.error_stale");
    }
    return t("admin.gtcc.ppl.invitations.write.error");
  })();

  const submitInvite = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const email = inviteEmail.trim();
    if (email === "" || writeBusy) return;
    setInviteSuccess(null);
    createInvitation.mutate(
      { email, role: inviteRole },
      {
        onSuccess: () => {
          setInviteSuccess(t("admin.gtcc.ppl.invitations.write.success_created"));
          setInviteEmail("");
          setInviteRole(DEFAULT_INVITE_ROLE);
        },
      },
    );
  };

  const total = members.data?.total ?? 0;
  const truncated = members.isSuccess && gtccMembersTruncated({ items: memberRows, total });

  return (
    <Stack gap={4} data-testid="gtcc-view-ppl" style={{ padding: "var(--gt-sys-space-5)" }}>
      <Stack gap={1}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.gtcc.ppl.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.gtcc.ppl.subtitle")}
        </p>
        <StatePill tone="info" label={t("admin.gtcc.ppl.write_scope")} />
      </Stack>

      {headTabs}

      <SectionCard
        testId="gtcc-ppl-members"
        headingId="gtcc-ppl-members-heading"
        title={t("admin.gtcc.ppl.members.title")}
        subtitle={t("admin.gtcc.ppl.members.subtitle")}
      >
        {members.isSuccess ? (
          <Stack gap={2} data-testid="gtcc-ppl-members-counts">
            <Inline gap={2} wrap>
              <StatePill tone="neutral" label={t("admin.gtcc.ppl.members.total", { total })} />
              {GTCC_MEMBER_STATUSES.map((status) => (
                <Count
                  key={status}
                  label={t(`admin.gtcc.ppl.member_status.${status}`)}
                  value={members.data.byStatus?.[status] ?? 0}
                  tone={MEMBER_STATUS_TONES[status]}
                />
              ))}
            </Inline>
            <Inline gap={2} wrap>
              {GTCC_MEMBER_ROLES.map((role) => (
                <Count
                  key={role}
                  label={t(`admin.gtcc.ppl.role.${role}`)}
                  value={members.data.byRole?.[role] ?? 0}
                  tone={ROLE_TONES[role]}
                />
              ))}
            </Inline>
            <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
              {t("admin.gtcc.ppl.members.counts_note")}
            </p>
            {truncated ? (
              <StatePill
                tone="warn"
                label={t("admin.gtcc.ppl.members.truncated", {
                  shown: memberRows.length,
                  total,
                })}
              />
            ) : null}
          </Stack>
        ) : null}

        {memberSuccess ? (
          <span role="status" data-testid="gtcc-ppl-members-success">
            {memberSuccess}
          </span>
        ) : null}

        <DataTable
          caption={t("admin.gtcc.ppl.members.table_caption")}
          columns={memberColumns}
          rows={memberRows}
          getRowId={(row) => row.id}
          loading={members.isPending}
          loadingLabel={t("admin.gtcc.ppl.members.loading")}
          error={membersError}
          empty={{ title: t("admin.gtcc.ppl.members.empty") }}
          testId="gtcc-ppl-members-table"
        />
      </SectionCard>

      <SectionCard
        testId="gtcc-ppl-invitations"
        headingId="gtcc-ppl-invitations-heading"
        title={t("admin.gtcc.ppl.invitations.title")}
        subtitle={t("admin.gtcc.ppl.invitations.subtitle")}
      >
        <form onSubmit={submitInvite} data-testid="gtcc-ppl-invite-form">
          <Stack gap={2}>
            <Inline gap={2} wrap style={{ alignItems: "flex-end" }}>
              <TextInput
                type="email"
                required
                value={inviteEmail}
                disabled={writeBusy}
                label={t("admin.gtcc.ppl.invitations.write.email_label")}
                data-testid="gtcc-ppl-invite-email"
                onChange={(event) => setInviteEmail(event.currentTarget.value)}
              />
              <label>
                {t("admin.gtcc.ppl.invitations.write.role_label")}{" "}
                <select
                  value={inviteRole}
                  disabled={writeBusy}
                  data-testid="gtcc-ppl-invite-role"
                  style={SELECT_STYLE}
                  onChange={(event) => setInviteRole(event.currentTarget.value as GtccMemberRole)}
                >
                  {GTCC_MEMBER_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {t(`admin.gtcc.ppl.role.${role}`)}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                type="submit"
                variant="primary"
                disabled={writeBusy}
                loading={createInvitation.isPending}
                data-testid="gtcc-ppl-invite-submit"
              >
                {createInvitation.isPending
                  ? t("admin.gtcc.ppl.invitations.write.submitting")
                  : t("admin.gtcc.ppl.invitations.write.submit")}
              </Button>
            </Inline>
            {inviteError ? (
              <span
                role="alert"
                data-testid="gtcc-ppl-invite-error"
                style={{ color: "var(--gt-sys-danger)" }}
              >
                {inviteError}
              </span>
            ) : null}
            {rowActionError ? (
              <span
                role="alert"
                data-testid="gtcc-ppl-invitations-row-error"
                style={{ color: "var(--gt-sys-danger)" }}
              >
                {rowActionError}
              </span>
            ) : null}
            {inviteSuccess ? (
              <span role="status" data-testid="gtcc-ppl-invitations-success">
                {inviteSuccess}
              </span>
            ) : null}
          </Stack>
        </form>

        {invitations.isSuccess ? (
          <Inline gap={2} wrap data-testid="gtcc-ppl-invitations-counts">
            {GTCC_INVITATION_STATUSES.map((status) => (
              <Count
                key={status}
                label={t(`admin.gtcc.ppl.invitation_status.${status}`)}
                value={invitationCounts[status]}
                tone={INVITATION_STATUS_TONES[status]}
              />
            ))}
          </Inline>
        ) : null}

        <DataTable
          caption={t("admin.gtcc.ppl.invitations.table_caption")}
          columns={invitationColumns}
          rows={invitationRows}
          getRowId={(row) => row.id}
          loading={invitations.isPending}
          loadingLabel={t("admin.gtcc.ppl.invitations.loading")}
          error={invitationsError}
          empty={{ title: t("admin.gtcc.ppl.invitations.empty") }}
          testId="gtcc-ppl-invitations-table"
        />
      </SectionCard>

      <SectionCard
        testId="gtcc-ppl-writes"
        headingId="gtcc-ppl-writes-heading"
        title={t("admin.gtcc.ppl.writes.title")}
        subtitle={t("admin.gtcc.ppl.writes.subtitle")}
      >
        <StatePill tone="warn" label={t("admin.gtcc.ppl.writes.absent")} />
        <p style={{ margin: 0 }}>{t("admin.gtcc.ppl.writes.body")}</p>
      </SectionCard>

      <ConfirmDialog
        open={pendingRevoke !== null}
        title={t("admin.gtcc.ppl.invitations.write.confirm_title")}
        body={t("admin.gtcc.ppl.invitations.write.confirm_body", {
          email: pendingRevoke?.email ?? "",
        })}
        confirmLabel={t("admin.gtcc.ppl.invitations.write.confirm_ok")}
        cancelLabel={t("admin.gtcc.ppl.invitations.write.confirm_cancel")}
        tone="danger"
        pending={revokeInvitation.isPending}
        errorMessage={rowActionError}
        testIdPrefix="gtcc-ppl-revoke-confirm"
        onCancel={() => setPendingRevoke(null)}
        onConfirm={() => {
          const target = pendingRevoke;
          if (!target) return;
          revokeInvitation.mutate(target.id, {
            onSuccess: () => {
              setInviteSuccess(t("admin.gtcc.ppl.invitations.write.success_revoked"));
              setPendingRevoke(null);
            },
          });
        }}
      />

      <ConfirmDialog
        open={pendingAction !== null}
        title={t("admin.gtcc.ppl.members.write.confirm_title")}
        body={confirmBody}
        confirmLabel={t("common.confirm")}
        cancelLabel={t("common.cancel")}
        tone={pendingAction?.kind === "suspend" ? "danger" : "default"}
        pending={writePending}
        errorMessage={writeError}
        onConfirm={runAction}
        onCancel={closeDialog}
        testIdPrefix="gtcc-ppl-member-confirm"
      />
    </Stack>
  );
}
